import { supabase } from '../supabaseClient';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export const reviewService = {
  /**
   * Check if current user is a reviewer
   * Anyone with access to the system can review
   */
  async isReviewer(): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    return !!user; // Anyone authenticated can review
  },

  /**
   * Submit contract for review (sends email to selected reviewers)
   */
  async submitForReview(
    contractId: string, 
    reviewers: Array<{ email: string; name: string }>
  ): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch(`${SUPABASE_URL}/functions/v1/send-review-email`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ contractId, reviewers }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send review email');
    }

    return await response.json();
  },

  /**
   * Approve the internal review of a contract.
   *
   * Internal-review approval does NOT advance the contract's status —
   * status stays at 'internal_review' until the user explicitly sends
   * the contract to the client (the client_review transition happens
   * inside send-contract-review-email). Approve here just signals that
   * Privion-side sign-off is done:
   *   - closes the current user's open review tasks for this contract
   *   - stamps an audit entry on contract.metadata.internal_review_approvals[]
   */
  async approveContract(contractId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Close the reviewer's tasks. Trigger-driven completion only fires on a
    // status change, so we update manually here. Limited to this user's
    // tasks since multi-reviewer setups should track per-person approval.
    const { error: taskError } = await supabase
      .from('tasks')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        completed_by: user.id,
      })
      .eq('contract_id', contractId)
      .eq('assigned_to', user.id)
      .eq('task_type', 'review')
      .in('status', ['pending', 'in_progress']);
    if (taskError) throw taskError;

    // Audit on the contract itself so a future "ready to send" badge can
    // detect approval without joining the tasks table.
    const { data: c, error: readError } = await supabase
      .from('contracts')
      .select('metadata')
      .eq('id', contractId)
      .single();
    if (readError) throw readError;

    const meta = (c.metadata as Record<string, unknown> | null) ?? {};
    const approvals = Array.isArray(meta.internal_review_approvals)
      ? (meta.internal_review_approvals as Array<Record<string, unknown>>)
      : [];
    const nextMeta = {
      ...meta,
      internal_review_approvals: [
        ...approvals.filter((a) => a.user_id !== user.id),
        { user_id: user.id, approved_at: new Date().toISOString() },
      ],
    };

    const { error: updateError } = await supabase
      .from('contracts')
      .update({ metadata: nextMeta })
      .eq('id', contractId);
    if (updateError) throw updateError;
  },

  /**
   * Reject contract (move back to draft)
   */
  async rejectContract(contractId: string): Promise<void> {
    const { error } = await supabase
      .from('contracts')
      .update({ status: 'draft' })
      .eq('id', contractId);

    if (error) throw error;
  },
};

